import React, { useContext, useState } from "react";
import Box from "@mui/material/Box";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import { ConfirmStatus } from "./ConfirmStatus";
import { ConfirmOrder } from "./ConfirmOrder";
import { NotificationType } from "../../../constants/global";
import { ToasterContext } from "../../../components/shared/Toaster/ToasterContext";
import { CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../../navigation/routeConfig";
import { FedexPickupType } from "../../../constants/shipping";
import { useMutationUpdateManyOrder } from "../../../queries/order/hooks/useMutationUpdateManyOrder";
import axios from "axios";

interface SellerConfirmationDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setSuccessOrderId: (successOrderId: null | number) => void;
  data: any;
  refetch: any;
  orderNumber: number;
}

const LOT_EXP_EDIT_INFO_PARAGRAPHS = [
  "Before confirming, verify each item's Lot Number and Expiration Date match the actual product being shipped. This is required for DSCSA compliance.",
  "If the actual expiration date is shorter, mark the item Out of stock. Mismatched lot or expiration information may cause order return, penalties, or platform restriction.",
];

export const SellerConfirmationDialog: React.FunctionComponent<SellerConfirmationDialogProps> = ({ open, setOpen, setSuccessOrderId, data, refetch, orderNumber }) => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToasterContext);
  const [activeStatus, setActiveStatus] = useState<string>("CONFIRM_STATUS");
  const defaultAvailableItems = data.filter((dataItem: any) => dataItem.orderNumber === orderNumber);
  const [availableItems, setAvailableItems] = useState<any>(defaultAvailableItems);
  const [fedExInsurance, setFedExInsurance] = useState<number>(0);
  const [fedExPickupType, setFedExPickupType] = useState<string>(FedexPickupType.dropOffAtFedexLocation);
  const [fedExPickupDate, setFedExPickupDate] = useState<string>("");
  const [savingEdits, setSavingEdits] = useState<boolean>(false);

  const [updateManyOrder, { loading }] = useMutationUpdateManyOrder();

  const hasInvalidEdits = availableItems.some((item: any) => item.status === 1 && item.editInvalid);

  const handleSetActive = (value: string) => {
    setActiveStatus(value);
  };

  const handleNext = () => {
    setActiveStatus("CONFIRM_ORDER");
  };

  const hasAvailableItems = (availableItems: any) => {
    let isAvailableItems = false;

    availableItems.forEach((availableItem: any) => {
      if (availableItem.status === 1) {
        isAvailableItems = true;
      }
    });

    return isAvailableItems;
  };

  const handleConfirmOrder = async () => {
    const outOfStockIds = availableItems.filter((item: any) => item.status === 2).map((item: any) => item.id);
    const availableIds = availableItems.filter((item: any) => item.status === 1).map((item: any) => item.id);

    // Persist lot number / exp date edits before confirming the order
    const editedItems = availableItems
      .filter((item: any) => item.status === 1 && (item.editedLotNumber !== undefined || item.editedExpDate !== undefined))
      .map((item: any) => ({
        orderId: item.id,
        lotNumber: item.editedLotNumber ?? (item.OrderItemSnapshot?.lotNumber ?? item.Product?.lotNumber ?? ""),
        expDate: item.editedExpDate ?? (item.OrderItemSnapshot?.expDate ?? item.Product?.expDate),
      }));

    if (editedItems.length > 0) {
      setSavingEdits(true);
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/v2/order/edit/confirmationDetails`, editedItems);
      } catch (error: any) {
        const message = error?.response?.data?.message || error.message;
        addToast({ type: NotificationType.Error, title: Array.isArray(message) ? message.join(", ") : message });
        setSavingEdits(false);
        return;
      }
      setSavingEdits(false);
    }

    const availableRequestData: any = {
      status: { set: 1 },
      isConfirmed: { set: true },
      fedExInsurance: { set: `${fedExInsurance}` },
      fedExPickupType: { set: fedExPickupType },
      shippingStatus: { set: 1 },
    };
    const outOfStockRequestData: any = {
      status: { set: 2 },
      isConfirmed: { set: true },
    };


    // if order is edited disable products
    if (data?.[0]?.loopStatus === "pending_seller_last") {
      const productIDs = availableItems.map((item: any) => item.productId);
      const disableProductApi = `${process.env.REACT_APP_API_URL}/v2/order/edit/disableProduct`;
      const disableProductRequestData = {
        productIDs,
      };
      axios
        .post(disableProductApi, disableProductRequestData)
        .then(() => {})
        .catch((error) => {
          addToast({ type: NotificationType.Error, title: error.message });
        });
    }

    if (outOfStockIds.length > 0) {
      updateManyOrder({
        variables: {
          data: outOfStockRequestData,
          where: { id: { in: outOfStockIds } },
        },
      })
        .then(() => {
          setSuccessOrderId(null);
          if (availableIds.length === 0) {
            setOpen(false);
            refetch();
            navigate(routeConfigs.sellHistory.path);
          }
        })
        .catch((error: any) => {
          addToast({ type: NotificationType.Error, title: error.message });
        });
    }

    if (availableIds.length > 0) {
      updateManyOrder({
        variables: {
          data: availableRequestData,
          where: { id: { in: availableIds } },
          pickupDate: fedExPickupDate,
        },
      })
        .then(() => {
          setOpen(false);
          setSuccessOrderId(availableIds[0]);
        })
        .catch((error: any) => {
          addToast({ type: NotificationType.Error, title: error.message });
        });
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={() => setOpen(false)} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">Seller Confirmation</DialogTitle>
      <DialogContent sx={{ height: "600px", position: "relative", px: 2, pt: 1.5, bgcolor: "grey.50" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, mb: 1 }}>
          <Button
            fullWidth
            variant={activeStatus === "CONFIRM_STATUS" ? "contained" : "outlined"}
            onClick={() => {
              setAvailableItems(defaultAvailableItems);
              handleSetActive("CONFIRM_STATUS");
            }}
            disabled={!hasAvailableItems(availableItems)}
          >
            Confirm Status
          </Button>
          <Button
            fullWidth
            sx={{ ml: 2 }}
            variant={activeStatus === "CONFIRM_ORDER" ? "contained" : "outlined"}
            onClick={() => handleSetActive("CONFIRM_ORDER")}
            disabled={availableItems.length === 0 || !hasAvailableItems(availableItems)}
          >
            Confirm Order
          </Button>
        </Box>
        <Alert
          severity="info"
          sx={{
            mb: 1,
            py: 0.75,
            color: "text.primary",
            borderLeft: "3px solid",
            borderLeftColor: "info.main",
            "& .MuiAlert-message": {
              py: 0.25,
              fontSize: 13,
              lineHeight: 1.45,
              fontWeight: 500,
            },
            "& .MuiAlert-icon": { alignItems: "center" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box>
              <Box component="span" sx={{ fontWeight: 700 }}>
                ATTENTION:{" "}
              </Box>
              {LOT_EXP_EDIT_INFO_PARAGRAPHS[0]}
            </Box>
            {LOT_EXP_EDIT_INFO_PARAGRAPHS.slice(1).map((paragraph) => (
              <Box key={paragraph}>{paragraph}</Box>
            ))}
          </Box>
        </Alert>
        {activeStatus === "CONFIRM_STATUS" && (
          <ConfirmStatus data={data.filter((dataItem: any) => dataItem.orderNumber === orderNumber)} availableItems={availableItems} setAvailableItems={setAvailableItems} />
        )}
        {activeStatus === "CONFIRM_ORDER" && hasAvailableItems(availableItems) && (
          <ConfirmOrder availableItems={availableItems} setFedExInsurance={setFedExInsurance} setFedExPickupType={setFedExPickupType} setFedExPickupDate={setFedExPickupDate} />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          fullWidth
          variant="contained"
          onClick={() => (activeStatus === "CONFIRM_STATUS" && hasAvailableItems(availableItems) ? handleNext() : handleConfirmOrder())}
          disabled={availableItems.length === 0 || loading || savingEdits || hasInvalidEdits}
        >
          {activeStatus === "CONFIRM_STATUS" && hasAvailableItems(availableItems) ? "Next" : "Confirm Order"}
          {(activeStatus === "CONFIRM_ORDER" || !hasAvailableItems(availableItems)) && (loading || savingEdits) && <CircularProgress sx={{ ml: 2 }} size={14} color="inherit" />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
